import React, { useMemo } from 'react'
import {
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { AdminDialog } from '@/components/AdminDialog'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import TransportTruckCard from '../cards/TransportTruckCard'
import { useLogistics, useRepairTrucksForTrucks } from '@/hooks/logistics/useLogistics'
import { useClientUsers } from '@/hooks/useUsers'
import { usePickupLocations } from '@/hooks/inventory/usePickupLocations'
import { useRepairShopLocations } from '@/hooks/inventory/useRepairShopLocations'
import useAdminTransporter from '@/hooks/admin/useAdminTransporter'

interface TransportTruckCardDialogProps {
  open: boolean
  onClose: () => void
  truckId: string | null
}

const TransportTruckCardDialog: React.FC<TransportTruckCardDialogProps> = ({
  open,
  onClose,
  truckId,
}) => {
  const isMobile = useIsMobile()
  const { transportTrucksWithVehicles, isLoadingTransportTrucks } = useLogistics()
  const { clientUsers } = useClientUsers()
  const { pickupLocations } = usePickupLocations()
  const { repairShopLocations } = useRepairShopLocations()
  const { transporterGroups = [], transporters = [] } = useAdminTransporter()

  const truckWithVehicle = useMemo(
    () => transportTrucksWithVehicles.find(entry => entry.truck.id === truckId) ?? null,
    [transportTrucksWithVehicles, truckId],
  )

  const repairTrucksMap = useRepairTrucksForTrucks(
    truckWithVehicle ? [truckWithVehicle.truck.id] : [],
  )

  const clientUsersById = useMemo(
    () => new Map(clientUsers.map(user => [user.id, user])),
    [clientUsers],
  )

  const pickupLocationsById = useMemo(
    () =>
      new Map(
        (pickupLocations ?? [])
          .filter((location): location is typeof location & { id: string } => Boolean(location.id))
          .map(location => [location.id, location]),
      ),
    [pickupLocations],
  )

  const repairShopLocationsById = useMemo(
    () =>
      new Map(
        (repairShopLocations ?? [])
          .filter((location): location is typeof location & { id: string } => Boolean(location.id))
          .map(location => [location.id, location]),
      ),
    [repairShopLocations],
  )

  const vehicleLabel = truckWithVehicle?.vehicle
    ? `${truckWithVehicle.vehicle.year ?? ''} ${truckWithVehicle.vehicle.make ?? ''} ${truckWithVehicle.vehicle.model ?? ''}`.trim()
    : ''

  return (
    <AdminDialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          ...(isMobile ? { height: '100dvh', maxHeight: '100dvh' } : { borderRadius: 2 }),
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant='h6' fontWeight={700} noWrap>
            Transport card
          </Typography>
          {vehicleLabel && (
            <Typography variant='body2' color='text.secondary' noWrap>
              {vehicleLabel}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label='Close' size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          bgcolor: 'background.default',
          p: { xs: 1.5, md: 2 },
          ...(isMobile ? { flex: 1, minHeight: 0, overflow: 'auto' } : {}),
        }}
      >
        {isLoadingTransportTrucks ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : !truckWithVehicle ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              Transport truck not found.
            </Typography>
          </Box>
        ) : (
          <TransportTruckCard
            truckWithVehicle={truckWithVehicle}
            repairTrucksMap={repairTrucksMap}
            clientUsersById={clientUsersById}
            pickupLocationsById={pickupLocationsById}
            repairShopLocationsById={repairShopLocationsById}
            transporterGroups={transporterGroups}
            transporters={transporters}
          />
        )}
      </DialogContent>
    </AdminDialog>
  )
}

export default TransportTruckCardDialog
